import React, { useState, useContext } from 'react';
import { LanguageContext } from '../../../App';
import { Modal } from '../../ui/Modal';
import { MessageSquareIcon, ChevronDownIcon, PlusIcon, XIcon } from 'lucide-react';
interface MessageAllModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export const MessageAllModal: React.FC<MessageAllModalProps> = ({
  isOpen,
  onClose
}) => {
  const {
    language
  } = useContext(LanguageContext);
  const [messageType, setMessageType] = useState('all');
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  // Sample data
  const drivers = [{
    id: 'DRV-1001',
    name: 'Mohammed Al-Sabah'
  }, {
    id: 'DRV-1002',
    name: 'Ahmed Al-Enezi'
  }, {
    id: 'DRV-1003',
    name: 'Fatima Al-Qadiri'
  }, {
    id: 'DRV-1004',
    name: 'Khalid Al-Mutairi'
  }, {
    id: 'DRV-1005',
    name: 'Sarah Al-Sabah'
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle message submission
    // In a real app, this would send data to an API
    // Close the modal after submission
    onClose();
  };
  const toggleDriverSelection = (driverId: string) => {
    if (selectedDrivers.includes(driverId)) {
      setSelectedDrivers(selectedDrivers.filter(id => id !== driverId));
    } else {
      setSelectedDrivers([...selectedDrivers, driverId]);
    }
  };
  const removeSelectedDriver = (driverId: string) => {
    setSelectedDrivers(selectedDrivers.filter(id => id !== driverId));
  };
  const getDriverName = (driverId: string) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver ? driver.name : driverId;
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={language === 'en' ? 'Send Message to Drivers' : 'إرسال رسالة للسائقين'} size="md" data-id="element-515">
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-516">
        {/* Message Type */}
        <div data-id="element-517">
          <label htmlFor="messageType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-518">
            {language === 'en' ? 'Message To' : 'إرسال إلى'}
          </label>
          <div className="relative" data-id="element-519">
            <select id="messageType" value={messageType} onChange={e => {
            setMessageType(e.target.value);
            if (e.target.value !== 'selected') {
              setSelectedDrivers([]);
            }
          }} className="appearance-none block w-full px-10 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" data-id="element-520">
              <option value="all" data-id="element-521">
                {language === 'en' ? 'All Drivers' : 'جميع السائقين'}
              </option>
              <option value="active" data-id="element-522">
                {language === 'en' ? 'Active Drivers Only' : 'السائقين النشطين فقط'}
              </option>
              <option value="selected" data-id="element-523">
                {language === 'en' ? 'Selected Drivers' : 'سائقين محددين'}
              </option>
            </select>
            <MessageSquareIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-524" />
            <ChevronDownIcon size={16} className="absolute right-3 top-3 text-gray-400" data-id="element-525" />
          </div>
        </div>
        {/* Driver Selection (conditional) */}
        {messageType === 'selected' && <div className="space-y-3" data-id="element-526">
            <div className="flex justify-between items-center" data-id="element-527">
              <label className="block text-sm font-medium text-gray-700" data-id="element-528">
                {language === 'en' ? 'Select Drivers' : 'اختر السائقين'}
              </label>
              <button type="button" onClick={() => setSelectedDrivers(drivers.map(d => d.id))} className="text-xs text-congress-blue-600 hover:text-congress-blue-800" data-id="element-529">
                {language === 'en' ? 'Select All' : 'اختر الكل'}
              </button>
            </div>
            {/* Selected drivers display */}
            {selectedDrivers.length > 0 && <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-md bg-gray-50" data-id="element-530">
                {selectedDrivers.map(driverId => <div key={driverId} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-congress-blue-100 text-congress-blue-800" data-id="element-531">
                    {getDriverName(driverId)}
                    <button type="button" onClick={() => removeSelectedDriver(driverId)} className="ml-1 text-congress-blue-600 hover:text-congress-blue-800" data-id="element-532">
                      <XIcon size={12} data-id="element-533" />
                    </button>
                  </div>)}
              </div>}
            {/* Driver selection list */}
            <div className="border border-gray-200 rounded-md overflow-hidden max-h-40 overflow-y-auto" data-id="element-534">
              {drivers.map(driver => <div key={driver.id} className="flex items-center px-3 py-2 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 cursor-pointer" onClick={() => toggleDriverSelection(driver.id)} data-id="element-535">
                  <input type="checkbox" checked={selectedDrivers.includes(driver.id)} onChange={() => {}} className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-536" />
                  <label className="ml-2 block text-sm text-gray-700" data-id="element-537">
                    {driver.name} ({driver.id})
                  </label>
                </div>)}
            </div>
          </div>}
        {/* Message Subject */}
        <div data-id="element-538">
          <label htmlFor="messageSubject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-539">
            {language === 'en' ? 'Subject' : 'الموضوع'}
          </label>
          <input type="text" id="messageSubject" className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" placeholder={language === 'en' ? 'Enter subject' : 'أدخل الموضوع'} required data-id="element-540" />
        </div>
        {/* Message Content */}
        <div data-id="element-541">
          <label htmlFor="messageContent" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-542">
            {language === 'en' ? 'Message' : 'الرسالة'}
          </label>
          <textarea id="messageContent" rows={4} className="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-congress-blue-500 focus:border-congress-blue-500" placeholder={language === 'en' ? 'Enter your message' : 'أدخل رسالتك'} required data-id="element-543"></textarea>
        </div>
        {/* Message Options */}
        <div className="space-y-3" data-id="element-544">
          <div className="flex items-center" data-id="element-545">
            <input id="requestReply" type="checkbox" className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-546" />
            <label htmlFor="requestReply" className="ml-2 block text-sm text-gray-700" data-id="element-547">
              {language === 'en' ? 'Request Reply' : 'طلب رد'}
            </label>
          </div>
          <div className="flex items-center" data-id="element-548">
            <input id="highPriority" type="checkbox" className="h-4 w-4 text-congress-blue-600 focus:ring-congress-blue-500 border-gray-300 rounded" data-id="element-549" />
            <label htmlFor="highPriority" className="ml-2 block text-sm text-gray-700" data-id="element-550">
              {language === 'en' ? 'High Priority' : 'أولوية عالية'}
            </label>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3" data-id="element-551">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-552">
            {language === 'en' ? 'Cancel' : 'إلغاء'}
          </button>
          <button type="submit" className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-congress-blue-600 hover:bg-congress-blue-700" disabled={messageType === 'selected' && selectedDrivers.length === 0} data-id="element-553">
            {language === 'en' ? 'Send Message' : 'إرسال الرسالة'}
          </button>
        </div>
      </form>
    </Modal>;
};